import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SSRConfig, DEFAULT_SSR_CONFIG } from '@/hooks/useSSRAlpha';

interface SSRSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: SSRConfig;
  onConfigChange: (config: SSRConfig) => void;
}

export function SSRSettingsDialog({ open, onOpenChange, config, onConfigChange }: SSRSettingsDialogProps) {
  const [localConfig, setLocalConfig] = useState<SSRConfig>(config);

  const handleSave = () => {
    onConfigChange(localConfig);
    onOpenChange(false);
  };

  const handleReset = () => {
    setLocalConfig(DEFAULT_SSR_CONFIG);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>SSR Alpha Signal Settings</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Crypto Symbol */}
          <div className="space-y-2">
            <Label htmlFor="symbol">Crypto Symbol</Label>
            <Select
              value={localConfig.symbol}
              onValueChange={(v) => setLocalConfig({ ...localConfig, symbol: v as 'BTC' | 'ETH' })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BTC">BTC</SelectItem>
                <SelectItem value="ETH">ETH</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* SSR Settings Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">SSR SETTINGS</h4>
            
            <div className="space-y-2">
              <Label htmlFor="ssrLength">SSR SMA Length</Label>
              <Input
                id="ssrLength"
                type="number"
                min={10}
                max={500}
                value={localConfig.ssrLength}
                onChange={(e) => setLocalConfig({ ...localConfig, ssrLength: parseInt(e.target.value) || 200 })}
              />
            </div>
          </div>

          {/* RSI Settings Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">RSI SETTINGS</h4>
            
            <div className="space-y-2">
              <Label htmlFor="rsiLength">RSI Length</Label>
              <Input
                id="rsiLength"
                type="number"
                min={2}
                max={50}
                value={localConfig.rsiLength}
                onChange={(e) => setLocalConfig({ ...localConfig, rsiLength: parseInt(e.target.value) || 14 })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="rsiOverbought">Overbought Level</Label>
                <Input
                  id="rsiOverbought"
                  type="number"
                  min={50}
                  max={100}
                  value={localConfig.rsiOverbought}
                  onChange={(e) => setLocalConfig({ ...localConfig, rsiOverbought: parseInt(e.target.value) || 75 })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="rsiOversold">Oversold Level</Label>
                <Input
                  id="rsiOversold"
                  type="number"
                  min={0}
                  max={50}
                  value={localConfig.rsiOversold}
                  onChange={(e) => setLocalConfig({ ...localConfig, rsiOversold: parseInt(e.target.value) || 35 })}
                />
              </div>
            </div>
          </div>

          {/* Bollinger Bands Settings Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">BOLLINGER BANDS SETTINGS</h4>
            
            <div className="space-y-2">
              <Label htmlFor="bbLength">BB Length</Label>
              <Input
                id="bbLength"
                type="number"
                min={10}
                max={500}
                value={localConfig.bbLength}
                onChange={(e) => setLocalConfig({ ...localConfig, bbLength: parseInt(e.target.value) || 200 })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="bbUpperMult">BB Upper Std Dev</Label>
                <Input
                  id="bbUpperMult"
                  type="number"
                  min={0.5}
                  max={5}
                  step={0.1}
                  value={localConfig.bbUpperMult}
                  onChange={(e) => setLocalConfig({ ...localConfig, bbUpperMult: parseFloat(e.target.value) || 2.0 })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bbLowerMult">BB Lower Std Dev</Label>
                <Input
                  id="bbLowerMult"
                  type="number"
                  min={0.5}
                  max={5}
                  step={0.1}
                  value={localConfig.bbLowerMult}
                  onChange={(e) => setLocalConfig({ ...localConfig, bbLowerMult: parseFloat(e.target.value) || 1.8 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bbMaType">BB MA Type</Label>
              <Select
                value={localConfig.bbMaType}
                onValueChange={(v) => setLocalConfig({ ...localConfig, bbMaType: v as SSRConfig['bbMaType'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SMA">SMA</SelectItem>
                  <SelectItem value="EMA">EMA</SelectItem>
                  <SelectItem value="RMA">SMMA (RMA)</SelectItem>
                  <SelectItem value="WMA">WMA</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Period */}
          <div className="space-y-2">
            <Label htmlFor="period">Period</Label>
            <Select
              value={localConfig.period}
              onValueChange={(v) => setLocalConfig({ ...localConfig, period: v as SSRConfig['period'] })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1Y">1 Year</SelectItem>
                <SelectItem value="2Y">2 Years</SelectItem>
                <SelectItem value="3Y">3 Years</SelectItem>
                <SelectItem value="5Y">5 Years</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            Restaurar Padrões
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
